import React, { useState } from 'react';
import { get } from 'lodash';
import { useMountedState } from 'react-use';
import { Space, Switch, Spin, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { APP_DIMENSIONS } from '@utils/dimension/appDimensionHelper';
import { getDecodedStacktrace } from '@api/stackTrace';
import { getHighlightedText } from '../utils';
import { StyledCodeBlock, StyledMinifiedSwitch, StyledCodeBlockLoading } from '../styled';

interface Props {
  // minified version
  encodedStackTrace: string;
  // unminified version
  decodedStackTrace?: string;
  isDecoded?: boolean;
  data: Record<string, any>;
  highlightText?: string;
  enableSwitch?: boolean;
  onChange?: (stackTrace: string, isDecoded: boolean) => void;
}

export const CodeBlock = (props: Props) => {
  const {
    data,
    isDecoded = false,
    encodedStackTrace,
    decodedStackTrace,
    highlightText,
    enableSwitch,
    onChange,
  } = props;
  const isMounted = useMountedState();
  const [isLoading, setIsLoading] = useState(false);

  const highlightedData = getHighlightedText(
    isDecoded ? decodedStackTrace : encodedStackTrace,
    highlightText,
  );

  const handleOnChange = (text: string, decoded: boolean) => {
    if (isMounted()) {
      onChange?.(text, decoded);
    }
  };

  const renderMinifiedSwitch = () => {
    if (!enableSwitch) return null;
    const handleChange = async (encoded: boolean) => {
      if (encoded) {
        handleOnChange(encodedStackTrace, false);
        return;
      }

      setIsLoading(true);
      try {
        let decodedData = decodedStackTrace;
        if (!decodedData) {
          const result = await getDecodedStacktrace<string>({
            stack_trace: data[APP_DIMENSIONS.STRACK_TRACE],
            platform: data[APP_DIMENSIONS.PLATFORM],
            app_id: data[APP_DIMENSIONS.APP_ID],
            application_version: data[APP_DIMENSIONS.APPLICATION_VERSION],
          });
          decodedData = result.data;
        }
        handleOnChange(decodedData, true);
      } catch (e: any) {
        const errorMsg = get(e, 'response.data.message', '');
        message.error(errorMsg || 'Failed to get decoded stack trace');
      }
      setIsLoading(false);
    };
    return (
      <StyledMinifiedSwitch>
        <Space align="center">
          <Switch onChange={handleChange} checked={!isDecoded} size="small" />
          <span
            style={{
              position: 'relative',
              top: '2px',
            }}
          >
            {`Minified ${isDecoded ? 'Off' : 'On'}`}
          </span>
        </Space>
      </StyledMinifiedSwitch>
    );
  };

  return (
    <StyledCodeBlock style={{ paddingTop: enableSwitch ? '35px' : '10px' }}>
      {isLoading && (
        <StyledCodeBlockLoading>
          <Spin indicator={<LoadingOutlined />} />
        </StyledCodeBlockLoading>
      )}
      {renderMinifiedSwitch()}
      <pre>{highlightedData}</pre>
    </StyledCodeBlock>
  );
};
